import React from 'react';
import { usePluginRoutes, usePluginNavigation, useReactRouterRoutes } from './usePluginRoutes';
import { RegisteredRoute } from './types';

/**
 * PluginRouter component that renders registered plugin routes
 * This is a framework-agnostic component that can be used with any routing solution
 */
export interface PluginRouterProps {
  namespace?: string;
  currentPath?: string;
  onNavigate?: (path: string) => void;
  fallback?: React.ComponentType<{ path: string }>;
  layout?: React.ComponentType<{ children: React.ReactNode; route: RegisteredRoute }>;
  className?: string;
  style?: React.CSSProperties;
}

export const PluginRouter: React.FC<PluginRouterProps> = ({
  namespace,
  currentPath = window?.location?.pathname || '/',
  onNavigate,
  fallback: FallbackComponent,
  layout: LayoutComponent,
  className,
  style
}) => {
  const { getRoutes } = usePluginRoutes();
  const routes = getRoutes(namespace);

  // Find the matching route
  const matchingRoute = routes.find(route => {
    if (route.exact) {
      return route.path === currentPath;
    }
    return currentPath.startsWith(route.path);
  });

  if (!matchingRoute) {
    if (FallbackComponent) {
      return <FallbackComponent path={currentPath} />;
    }
    return (
      <div className={className} style={style}>
        <h2>Route not found</h2>
        <p>No plugin route found for: {currentPath}</p>
      </div>
    );
  }

  const RouteComponent = matchingRoute.component;
  const routeElement = <RouteComponent />;

  if (LayoutComponent) {
    return (
      <div className={className} style={style}>
        <LayoutComponent route={matchingRoute}>
          {routeElement}
        </LayoutComponent>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      {routeElement}
    </div>
  );
};

/**
 * PluginNavigation component that renders navigation from registered routes
 */
export interface PluginNavigationProps {
  namespace?: string;
  onNavigate?: (path: string) => void;
  className?: string;
  style?: React.CSSProperties;
  itemClassName?: string;
  itemStyle?: React.CSSProperties;
  renderItem?: (item: any, isActive: boolean) => React.ReactNode;
  currentPath?: string;
}

export const PluginNavigation: React.FC<PluginNavigationProps> = ({
  namespace,
  onNavigate,
  className,
  style,
  itemClassName,
  itemStyle,
  renderItem,
  currentPath = window?.location?.pathname || '/'
}) => {
  const navigationItems = usePluginNavigation(namespace);

  const handleClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else if (typeof window !== 'undefined') {
      window.location.href = path;
    }
  };

  if (renderItem) {
    return (
      <nav className={className} style={style}>
        {navigationItems.map((item, index) => {
          const isActive = currentPath === item.path;
          return (
            <div key={item.path || index} onClick={() => handleClick(item.path)}>
              {renderItem(item, isActive)}
            </div>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className={className} style={style}>
      {navigationItems.map((item, index) => (
        <a
          key={item.path || index}
          href={item.path}
          onClick={(e) => {
            e.preventDefault();
            handleClick(item.path);
          }}
          className={`${itemClassName || ''} ${currentPath === item.path ? 'active' : ''}`}
          style={itemStyle}
          title={item.description}
        >
          {item.icon && <span className="icon">{item.icon}</span>}
          <span>{item.title}</span>
        </a>
      ))}
    </nav>
  );
};

/**
 * RouteList component for debugging and management purposes
 */
export interface RouteListProps {
  namespace?: string;
  showDetails?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const RouteList: React.FC<RouteListProps> = ({
  namespace,
  showDetails = false,
  className,
  style
}) => {
  const { getRoutes } = usePluginRoutes();
  const routes = getRoutes(namespace);

  return (
    <div className={className} style={style}>
      <h3>Registered Routes {namespace && `(${namespace})`}</h3>
      {routes.length === 0 ? (
        <p>No routes registered</p>
      ) : (
        <ul>
          {routes.map((route, index) => (
            <li key={route.id || index} style={{ marginBottom: '10px' }}>
              <strong>{route.path}</strong>
              {route.title && <span> - {route.title}</span>}
              <br />
              <small>Plugin: {route.pluginName}</small>
              {showDetails && (
                <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  <div>ID: {route.id}</div>
                  <div>Namespace: {route.namespace || 'default'}</div>
                  <div>Registered: {route.registeredAt.toLocaleString()}</div>
                  {route.exact && <div>Exact: true</div>}
                  {route.guard && <div>Has Guard: true</div>}
                  {route.order !== undefined && <div>Order: {route.order}</div>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Hook for React Router v6 integration
 */
export const usePluginRoutesV6 = (namespace?: string) => {
  const reactRouterRoutes = useReactRouterRoutes(namespace);
  
  // Convert to React Router v6 format
  return reactRouterRoutes.map(route => ({
    path: route.path,
    element: React.createElement(route.element),
    children: route.children?.map(child => ({
      path: child.path,
      element: React.createElement(child.element)
    }))
  }));
};